// src/dashboard/pages/teacher/Categories/CategoriesDashboard.jsx

import React, { useMemo } from "react";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useCategoriesDashboard } from "./hooks/useCategoriesDashboard";

import DashboardCategoriesHeader from "./components/DashboardCategoriesHeader";
import CategoriesFiltersCard from "./components/CategoriesFiltersCard";
import CategoriesKpiCards from "./components/CategoriesKpiCards";
import CategoriesDistributionPanel from "./components/CategoriesDistributionPanel";
import CategoriesMembersTable from "./components/CategoriesMembersTable";
import CategoriesMembersMobileCards from "./components/CategoriesMembersMobileCards";
import CategoriesEmptyState from "./components/CategoriesEmptyState";
import CategoryMembersDrawer from "./components/CategoryMembersDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Catégories & répartitions
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux enseignants et administrateurs.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function CategoriesDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const {
    filters,
    setFilters,

    loading,
    updatingWeightId,
    groupOptions,
    seasonOptions,
    yearOptions,
    ageCategoryOptions,

    stats,
    distributionRows,
    filteredMembers,
    counts,
    membersByBracket,

    drawerOpen,
    drawerTitle,
    drawerMembers,
    openMembersDrawer,
    closeMembersDrawer,

    handleUpdateWeight,
    refresh,
  } = useCategoriesDashboard();

  if (![1, 3].includes(roleId)) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardCategoriesHeader />

        <CategoriesFiltersCard
          filters={filters}
          setFilters={setFilters}
          loading={loading}
          groupOptions={groupOptions}
          seasonOptions={seasonOptions}
          yearOptions={yearOptions}
          ageCategoryOptions={ageCategoryOptions}
          onRefresh={refresh}
        />

        <CategoriesKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Répartition judo
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Catégories d’âge et de poids
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Visualisez les judokas par catégories officielles, groupes
                  morphologiques ou liste détaillée par adhérent.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Adhérents affichés :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredMembers.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 space-y-3">
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className="h-20 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : filteredMembers.length === 0 ? (
              <CategoriesEmptyState />
            ) : filters.viewMode === "distribution" ? (
              <CategoriesDistributionPanel
                rows={distributionRows}
                counts={counts}
                membersByBracket={membersByBracket}
                onOpenMembers={openMembersDrawer}
              />
            ) : (
              <div className="mt-5">
                <CategoriesMembersTable
                  members={filteredMembers}
                  onUpdateWeight={handleUpdateWeight}
                  updatingWeightId={updatingWeightId}
                />

                <CategoriesMembersMobileCards
                  members={filteredMembers}
                  onUpdateWeight={handleUpdateWeight}
                  updatingWeightId={updatingWeightId}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <CategoryMembersDrawer
        isOpen={drawerOpen}
        title={drawerTitle}
        members={drawerMembers}
        onClose={closeMembersDrawer}
      />
    </section>
  );
}
